/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Content, Header, HeaderTabs, Page } from '@backstage/core-components';
import { AuthorizationPolicyBody } from '../authorizationPolicy/AuthorizationPolicyBody';
import { NetworkPolicyBody } from '../networkPolicy/NetworkPolicyPage';

/**
 * One page for both access-control layers, because they are the same job at
 * two layers: NetworkPolicy scopes unmeshed peers at L4, Linkerd
 * AuthorizationPolicy scopes meshed callers per route at L7. Same audience,
 * same repos, same gitops files, and a service turning on both would otherwise
 * produce two PRs against the same env values file.
 *
 * The tab lives in the URL (/service-access/:tab) so a deep link survives a
 * reload and can be pasted into a ticket.
 */
const TABS = [
  { id: 'network', label: 'Network (L4)' },
  { id: 'authorization', label: 'Authorization (L7)' },
] as const;

const SUBTITLES: Record<string, string> = {
  network:
    'Ingress topology from gitops values (networkPolicy.dependencies.calledBy), edits open PRs',
  authorization:
    'Linkerd L7 route authorization from gitops values (linkerd.authorizationPolicy), read-only',
};

export const ServiceAccessPage = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  // Unknown tab falls back to network rather than 404ing: the old
  // /network-policy deep links redirect here and a typo should still land on
  // something useful.
  const index = useMemo(() => {
    const i = TABS.findIndex(t => t.id === tab);
    return i === -1 ? 0 : i;
  }, [tab]);

  return (
    <Page themeId="tool">
      <Header title="Service Access" subtitle={SUBTITLES[TABS[index].id]} />
      <HeaderTabs
        selectedIndex={index}
        onChange={i => navigate(`/service-access/${TABS[i].id}`)}
        tabs={TABS.map(t => ({ id: t.id, label: t.label }))}
      />
      <Content>
        {TABS[index].id === 'network' ? (
          <NetworkPolicyBody />
        ) : (
          <AuthorizationPolicyBody />
        )}
      </Content>
    </Page>
  );
};
